import { useQuery } from '@apollo/client'
import { useState } from 'react'
import { Button, ButtonColor, Loader } from '../../../components'
import {
  GET_APPLICANTS_BY_CAMPAIGN_UUID,
  GetApplicantsByCampaignUuidResponse,
} from '../../../gql'
import {
  Campaign,
  GetCollaborationsSort,
  QueryGetApplicantsByCampaignUuidArgs,
} from '../../../gql/types'
import { ApplicantsList } from './ApplicantsList'

interface CampaignApplicantsPublicProps {
  campaignUuid: string
  campaign: Campaign
}

const CampaignApplicantsPublic = ({
  campaignUuid,
  campaign,
}: CampaignApplicantsPublicProps) => {
  const [showFavorites, setShowFavorites] = useState(true)
  const [page, setPage] = useState(0)
  const LIMIT = 20 // Number of items per page

  const { loading, error, data, refetch } = useQuery<
    GetApplicantsByCampaignUuidResponse,
    QueryGetApplicantsByCampaignUuidArgs
  >(GET_APPLICANTS_BY_CAMPAIGN_UUID, {
    variables: {
      uuid: campaignUuid,
      isFavorite: showFavorites ? true : undefined,
      sort: GetCollaborationsSort.newest,
      skip: page * LIMIT,
      limit: LIMIT,
    },
  })

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  // Use data from query
  const finalData = data?.getApplicantsByCampaignUuid
  const finalLoading = loading
  const finalError = error

  if (finalError) {
    console.log(finalError)
    return <p>There has been an error. Try refreshing the page</p>
  }

  if (finalLoading) {
    return <Loader />
  }

  if (!finalLoading && !finalData && !campaign) {
    return <p>Campaign does not exist.</p>
  }

  // Used as a child component
  return (
    <>
      {/* Conditional filter buttons */}
      <div className='px-6 py-3'>
        <div className='flex justify-between items-center'>
          <h2 className='text-lg font-semibold'>
            {showFavorites
              ? 'Shortlisted Applicants'
              : 'All Campaign Applicants'}
          </h2>
          <Button
            title={
              showFavorites
                ? 'View all applicants'
                : 'View shortlisted applicants'
            }
            type='button'
            colour={showFavorites ? ButtonColor.secondary : ButtonColor.primary}
            onClick={() => {
              setShowFavorites(!showFavorites)
              setPage(0) // Reset to first page when toggling
            }}
          />
        </div>
      </div>

      {!finalLoading && renderApplicantsList()}
    </>
  )

  function renderApplicantsList() {
    return (
      <div className='pb-4 w-auto h-fit'>
        <div className='overflow-x-auto'>
          <div className='inline-block min-w-full align-middle'>
            <div>
              <ApplicantsList
                collaborations={finalData?.items}
                pagination={finalData?.pagination}
                setPage={handlePageChange}
                onRefetch={() => refetch()}
                showFavorites={showFavorites}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }
}

export { CampaignApplicantsPublic }
